// ─────────────────────────────────────────────────────────────────────────────
// Salesforce Events Service — disparo central dos eventos sf_* (todas plataformas)
// Web: delega a SalesforceMarketingCloudService → web-events (backend | direct)
// Mapa: docs/SALESFORCE_INTEGRATION.md
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from "@angular/core";
import { getSalesforceWebTransport } from "@app/config/salesforce.config";
import { sfLog } from "@app/core/salesforce/salesforce-debug.logger";
import { SalesforceMarketingCloudService } from "@app/services/salesforce-marketing-cloud.service";
import { SharedService } from "@app/services/shared.service";
import {
  SalesforceEventMap,
  SalesforceEventName,
  SfBuscaFiltroParams,
  SfBuscaParams,
  SfAdicionarCarrinhoParams,
  SfRemoverCarrinhoParams,
  SfPagamentoParams,
  SfCompraParams,
  SfCarrinhoParams,
  SfPageViewParams,
  SfCadastroParams,
  SfCadastroEmpresaParams,
} from "./salesforce-events.types";
import {
  SALESFORCE_EVENT_SCHEMA,
  SalesforceAttributeSchema,
} from "./salesforce-events.schema";
import { routeToSfPageViewNome, shouldTrackSfPageView } from "./salesforce-events.mappers";

/** Estilo dos logs Salesforce — laranja, consistente em todo o app. */
const SF_LOG_STYLE = "color: #FF8C00; font-weight: bold;";

function createSfEventId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `sf-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Evento retido até o SDK ficar pronto. */
interface PendingSfEvent {
  eventName: SalesforceEventName;
  payload: Record<string, string | number | boolean>;
}

/**
 * Teto da fila de eventos pré-inicialização.
 *
 * Se o SDK nunca ficar pronto (plugin ausente, falha de init), a fila para de crescer e
 * descarta os mais antigos. 50 cobre com folga a navegação possível nos ~3,5s de startup
 * (2500ms do app.component + delayRegistration + waitForNativePlugin).
 */
const MAX_PENDING_EVENTS = 50;

/** Espera máxima pelo contact key antes de drenar a fila, para usuário autenticado. */
const CONTACT_KEY_WAIT_MS = 5000;
const CONTACT_KEY_POLL_MS = 250;

@Injectable({ providedIn: "root" })
export class SalesforceEventsService {
  private ready = false;
  private readonly pending: PendingSfEvent[] = [];

  constructor(
    private sfmc: SalesforceMarketingCloudService,
    private shared: SharedService,
  ) {}

  // ═══════════════════════════════════════════════════════════════════════════
  // Inicialização
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Deve ser chamado após o SDK do Salesforce MC ser inicializado.
   * Marca o serviço como pronto e drena os eventos retidos durante o startup.
   */
  markReady(): void {
    this.ready = true;
    const modo = this.sfmc.isWebTransport() ? "web" : "nativo";
    sfLog.ok("Serviço de eventos pronto para disparos", { modo });
    void this.flushPending();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Método-chave: disparo genérico type-safe
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Dispara um evento no Salesforce Marketing Cloud.
   *
   * - Type-safe: o TS garante que `params` bate com o evento.
   * - Fire-and-forget: nunca bloqueia a UI, nunca lança exceção.
   * - Log laranja no console com nome do evento + atributos.
   */
  track<E extends SalesforceEventName>(eventName: E, params: SalesforceEventMap[E]): void {
    const enriched = this.enrichCommonParams(eventName, params as Record<string, unknown>);
    const { payload, defaultedKeys } = this.buildSchemaPayload(eventName, enriched);

    console.log(`%c[Salesforce Events] >> ${eventName}`, SF_LOG_STYLE, payload);
    if (defaultedKeys.length > 0) {
      console.log(
        `%c[Salesforce Events]    (sem valor, enviados com fallback: ${defaultedKeys.join(", ")})`,
        SF_LOG_STYLE,
      );
    }

    if (!this.ready) {
      this.enqueue(eventName, payload);
      return;
    }

    this.dispatch(eventName, payload);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Fila de startup
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Retém o evento até `markReady()`.
   *
   * Antes esses eventos eram descartados com um warn. Como o SDK só fica pronto ~3,5s
   * depois da UI, o `sf_page_view` da primeira tela se perdia em toda sessão.
   */
  private enqueue(
    eventName: SalesforceEventName,
    payload: Record<string, string | number | boolean>,
  ): void {
    if (this.pending.length >= MAX_PENDING_EVENTS) {
      const dropped = this.pending.shift();
      sfLog.warn(
        `Fila de eventos cheia (${MAX_PENDING_EVENTS}) — descartando o mais antigo: "${dropped?.eventName}"`,
      );
    }

    this.pending.push({ eventName, payload });
    sfLog.info(`Evento "${eventName}" enfileirado: SDK ainda não está pronto`, {
      naFila: this.pending.length,
    });
  }

  /**
   * Drena a fila. Idempotente: chamar com a fila vazia não faz nada.
   *
   * Aguarda o contact key quando há usuário autenticado — `markReady()` acontece ANTES do
   * `syncUserToSalesforce()` definir o contact key, e evento disparado nesse intervalo não
   * é atribuído ao contato no painel SFMC. A espera é limitada para não segurar eventos de
   * usuário anônimo (que nunca terá contact key) nem travar se o sync falhar.
   */
  private async flushPending(): Promise<void> {
    if (this.pending.length === 0) {
      return;
    }

    await this.waitForContactKeyIfAuthenticated();

    const queued = this.pending.splice(0, this.pending.length);
    sfLog.info(`Drenando ${queued.length} evento(s) retidos no startup`);

    for (const { eventName, payload } of queued) {
      this.dispatch(eventName, payload);
    }
  }

  private async waitForContactKeyIfAuthenticated(): Promise<void> {
    const clientId = String(this.shared.user?.clientId ?? "").trim();
    if (!clientId || this.sfmc.isWebTransport()) {
      return;
    }

    const deadline = Date.now() + CONTACT_KEY_WAIT_MS;
    while (Date.now() < deadline) {
      const contactKey = await this.sfmc.getContactKey();
      if (contactKey) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, CONTACT_KEY_POLL_MS));
    }

    sfLog.warn(
      "Contact key não ficou disponível a tempo — drenando a fila mesmo assim " +
        "(eventos podem não ser atribuídos ao contato no painel SFMC)",
      { clientId },
    );
  }

  /** Envio efetivo ao SDK. Fire-and-forget: nunca lança. */
  private dispatch(
    eventName: SalesforceEventName,
    payload: Record<string, string | number | boolean>,
  ): void {
    void this.sfmc.trackEvent(eventName, payload).then((ok) => {
      if (this.sfmc.isWebTransport() && getSalesforceWebTransport() === "log-only") {
        sfLog.info(`Evento "${eventName}" registrado apenas no console (web dev)`, payload);
        return;
      }
      if (ok) {
        sfLog.ok(`Evento entregue: ${eventName}`);
        return;
      }
      sfLog.error(`Evento não entregue: ${eventName}`, undefined, payload);
    }).catch((err) => {
      sfLog.error(`Falha ao entregar evento: ${eventName}`, err, payload);
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Eventos tipados — um método por evento para facilitar o uso
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * sf_busca_filtro
   * Disparado após seleção de todos os filtros para pesquisa no catálogo.
   */
  trackBuscaFiltro(params: SfBuscaFiltroParams): void {
    this.track("sf_busca_filtro", params);
  }

  /**
   * sf_busca
   * Disparado após pesquisar o nome do produto no filtro de pesquisa.
   */
  trackBusca(params: SfBuscaParams): void {
    this.track("sf_busca", params);
  }

  /**
   * sf_adicionar_carrinho
   * Disparado após adicionar produto ao carrinho.
   */
  trackAdicionarCarrinho(params: SfAdicionarCarrinhoParams): void {
    this.track("sf_adicionar_carrinho", params);
  }

  /**
   * sf_remover_carrinho
   * Disparado após remover produto do carrinho (lixeira ou diminuir quantidade).
   */
  trackRemoverCarrinho(params: SfRemoverCarrinhoParams): void {
    this.track("sf_remover_carrinho", params);
  }

  /**
   * sf_pagamento
   * Disparado ao confirmar o tipo de pagamento no checkout.
   */
  trackPagamento(params: SfPagamentoParams): void {
    this.track("sf_pagamento", params);
  }

  /**
   * sf_compra
   * Disparado ao clicar em "efetuar pedido".
   */
  trackCompra(params: SfCompraParams): void {
    this.track("sf_compra", params);
  }

  /**
   * sf_carrinho
   * Disparado ao visualizar a página de carrinho com itens.
   */
  trackCarrinho(params: SfCarrinhoParams): void {
    this.track("sf_carrinho", params);
  }

  /**
   * sf_page_view
   * Disparado ao visualizar páginas específicas (Home, Carrinho, Pedidos, etc.).
   */
  trackPageView(params: SfPageViewParams): void {
    this.track("sf_page_view", params);
  }

  /**
   * Rastreia sf_page_view a partir da URL da rota (com deduplicação).
   * Chamar a cada NavigationEnd, em paralelo ao analytics.
   */
  trackRoutePageView(url: string): void {
    if (!shouldTrackSfPageView(url)) {
      return;
    }
    const nome_pagina = routeToSfPageViewNome(url);
    if (nome_pagina) {
      this.trackPageView({ nome_pagina });
    }
  }

  /**
   * sf_cadastro
   * Disparado ao concluir dados do responsável no fluxo de cadastro.
   */
  trackCadastro(params: SfCadastroParams): void {
    this.track("sf_cadastro", params);
  }

  /**
   * sf_cadastro_empresa
   * Disparado após cadastro completo da empresa (incl. documentos e logística).
   */
  trackCadastroEmpresa(params: SfCadastroEmpresaParams): void {
    this.track("sf_cadastro_empresa", params);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Internals
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Injeta eventid / userid (schema ajustado) e nome_evento quando aplicável.
   * Caller pode sobrescrever passando os campos explicitamente.
   */
  private enrichCommonParams(
    eventName: SalesforceEventName,
    params: Record<string, unknown>,
  ): Record<string, unknown> {
    const userid = String(this.shared.user?.clientId ?? "").trim();
    const enriched: Record<string, unknown> = {
      ...params,
      eventid: (params.eventid as string) || createSfEventId(),
    };

    if (!enriched.userid && userid) {
      enriched.userid = userid;
    }

    const hasNomeEvento = SALESFORCE_EVENT_SCHEMA[eventName].some(
      (attribute) => attribute.name === "nome_evento",
    );
    if (hasNomeEvento && !enriched.nome_evento) {
      enriched.nome_evento = eventName;
    }

    return enriched;
  }

  /**
   * Produz exatamente as chaves do CSV. Campos sem valor também são enviados:
   * Text = "", Number = 0, Boolean = false.
   *
   * `null` não é usado como fallback porque o SDK nativo descarta null/undefined.
   */
  private buildSchemaPayload(
    eventName: SalesforceEventName,
    params: Record<string, unknown>,
  ): {
    payload: Record<string, string | number | boolean>;
    defaultedKeys: string[];
  } {
    const payload: Record<string, string | number | boolean> = {};
    const defaultedKeys: string[] = [];

    for (const attribute of SALESFORCE_EVENT_SCHEMA[eventName]) {
      const value = params[attribute.name];
      if (value === undefined || value === null || value === "") {
        payload[attribute.name] = this.defaultValueFor(attribute);
        defaultedKeys.push(attribute.name);
        continue;
      }
      payload[attribute.name] = this.coerceSchemaValue(attribute, value);
    }

    return { payload, defaultedKeys };
  }

  private defaultValueFor(attribute: SalesforceAttributeSchema): string | number | boolean {
    if (attribute.type === "Number") return 0;
    if (attribute.type === "Boolean") return false;
    return "";
  }

  private coerceSchemaValue(
    attribute: SalesforceAttributeSchema,
    value: unknown,
  ): string | number | boolean {
    if (attribute.type === "Number") {
      const numericValue = Number(value);
      return Number.isFinite(numericValue) ? numericValue : 0;
    }
    if (attribute.type === "Boolean") {
      return typeof value === "boolean" ? value : String(value).toLowerCase() === "true";
    }
    const textValue = String(value);
    return attribute.maxLength ? textValue.slice(0, attribute.maxLength) : textValue;
  }
}
